<template>
  <main ref="container"
    class="cw-mt-2.5 cw-mx-auto cw-mb-12 cw-p-0 cw-w-11/12 cw-flex cw-flex-wrap">
    <!-- Left Side Panel - Displayed if not explicitly disabled by a deep link -->
    <section v-if="!hidePane"
      class="cw-flex cw-flex-col cw-basis-full lg:cw-basis-2/5 xl:cw-basis-1/3 2xl:cw-basis-1/4 cw-h-[80vh]">
      <!-- Navigation tabs -->
      <ul class="cw-flex cw-text-white"
        role="tablist">
        <li v-for="tab in tabs"
          :key="tab"
          class="cw-px-2 cw-py-1 cw-cursor-pointer cw-rounded-t"
          :class="activeTab === tab ? 'cw-bg-blue-600': 'cw-bg-slate-800'"
          role="tab"
          @click="activeTab = tab">
          {{ tab }}
        </li>
      </ul>

      <locations-view v-if="activeTab === tabs[0]"
        :map-context="mapContext"
        :mapbox-config="mapboxConfig"
        :enabled-locations="enabledLocations"
        :locations-filtered-by-feature="locationsFilteredByFeature"
        :is-mobile-device="isMobileDevice"
        :deep-link-location="deepLinkLocation"
        :loading-complete="!locationsLoading && !ixLoading"
        @change="handleFilterOption" />

      <find-my-location-view v-else
        :enabled-locations="enabledLocations"
        :mapbox-config="mapboxConfig"
        :map-context="mapContext"
        :is-mobile-device="isMobileDevice" />
    </section>

    <!-- Right Side Panel: Map -->
    <section v-if="!isMobileDevice"
      class="cw-basis-full cw-pt-8 cw-h-[80vh]"
      :class="{ 'lg:cw-basis-3/5 xl:cw-basis-2/3 2xl:cw-basis-3/4': !hidePane }">
      <map-container ref="mapContainer"
        class="cw-w-full cw-h-[calc(80vh-2rem)] cw-relative cw-border cw-border-gray-200"
        :mapbox-config="mapboxConfig"
        :map-context="mapContext"
        :deep-link-location="deepLinkLocation"
        @setContext="setMapContext"
        @mapReady="setMapReadyStatus">
        <template v-if="isReady">
          <map-marker :map-context="mapContext"
            :location-list="locationsFilteredByFeature"
            @error="handleError" />
          <mapbox-geo-line :map-context="mapContext" />
          <map-popup :map-context="mapContext"
            :deep-link-location="deepLinkLocation" />
        </template>
        <!-- Map Legend -->
        <div id="location-legend"
          class="cw-absolute cw-top-2.5 cw-right-[70px] cw-bg-white cw-z-10 cw-shadow-md cw-border cw-border-gray-200 cw-rounded-lg cw-flex cw-items-center cw-px-3 cw-py-1"
          role="complementary"
          @mouseover="showTooltip"
          @mouseleave="hideTooltip">
          <!-- Enabled Location -->
          <img src="@/locations-map/assets/red-pin.png"
            alt="Megaport Enabled Location"
            class="cw-pr-1 cw-h-5">
          <span>{{ $t('enabled-locations-map.enabled-location') }}</span>
          <!-- Extended Location -->
          <template v-if="showExtended">
            <img src="@/locations-map/assets/plum-pin.png"
              alt="Megaport Extended Location"
              class="cw-pr-1 cw-h-5 cw-ml-2">
            <span title="Extended Location">
              {{ $t('enabled-locations-map.extended-location') }}
            </span>
          </template>
        </div>
        <!-- Map Legend Tooltip -->
        <div id="tooltip"
          role="tooltip"
          class="cw-p-4 cw-rounded cw-bg-white cw-absolute cw-top-0 cw-left-0 cw-z-10 cw-w-[300px] cw-border cw-border-gray-200 cw-shadow-md cw-hidden">
          <div id="arrow"
            class="cw-absolute cw-bg-white cw-w-[8px] cw-h-[8px] cw-transform cw-rotate-45 cw-border cw-border-gray-200" />
          {{ $t('enabled-locations-map.megaport-supported-location-types') }}
        </div>
        <!-- Learn More Bottom-Right Corner Link -->
        <p class="cw-absolute cw-bottom-2 cw-right-2 cw-z-50 cw-leading-4">
          <a href="https://docs.megaport.com/enabled-locations/"
            target="_blank"
            class="cw-text-blue-700">{{ $t('enabled-locations-map.learn-about-enabled-extended-locations') }}</a>
        </p>
      </map-container>
    </section>
  </main>
</template>

<script>
import {
  computePosition,
  flip,
  shift,
  offset,
  arrow,
  autoUpdate,
} from '@floating-ui/dom'
import LocationsView from '@/locations-map/components/locations/LocationsView'
import MapContainer from '@/locations-map/components/common/MapContainer'
import MapMarker from '@/locations-map/components/common/MapMarker'
import MapPopup from '@/locations-map/components/common/MapPopup'
import MapboxGeoLine from '@/locations-map/components/common/MapboxGeoLine'
import FindMyLocationView from '@/locations-map/components/find-my-location/FindMyLocationView'
import axios from 'axios'

export default {
  name: 'LocationsMap',

  components: {
    'locations-view': LocationsView,
    'map-container': MapContainer,
    'map-marker': MapMarker,
    'map-popup': MapPopup,
    'mapbox-geo-line': MapboxGeoLine,
    'find-my-location-view': FindMyLocationView,
  },

  data() {
    return {
      error: null,
      mapReady: false,
      mapContext: {},
      locationsLoading: true,
      locationsList: [],
      ixLoading: true,
      ixList: [],
      filterOptions: [],
      deepLinkLocation: {},
      hidePane: false,
      activeTab: this.$t('enabled-locations-map.locations'),
      tooltipCleanup: null,
      mapboxConfig: {
        style: process.env.VUE_APP_MAPBOX_STYLE,
        mapboxToken: process.env.VUE_APP_MAPBOX_TOKEN,
        language: this.$i18n.locale,
      },
      showExtended: process.env.VUE_APP_API_STATUS.includes('Extended'),
    }
  },

  computed: {
    isReady() {
      return !this.locationsLoading && !this.ixLoading && this.mapReady
    },
    enabledLocations() {
      return this.locationsList.filter(({ latitude, longitude, status }) => status !== 'Restricted' && [latitude, longitude].every(coord => Number.isFinite(coord)))
    },
    locationsFilteredByFeature() {
      if (this.filterOptions.length < 1) return this.enabledLocations

      return this.enabledLocations.filter(location => this.filterOptions.every(option => {
        switch (option) {
          case 'enabledLocations':
            return location.status === 'Active'
          case 'mcrLocations':
            return location.products.mcrVersion === 2
          case '100GLocations':
            return location.products.megaport.includes(100)
          case 'mveLocations':
            return 'mve' in location.products
          case 'localIxLocations':
            return this.ixMetros.includes(location.metro)
        }
      }))
    },
    isMobileDevice() {
      return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    },
    ixMetros() {
      return this.ixList.map(ix => ix.metro)
    },
    tabs() {
      return [
        this.$t('enabled-locations-map.locations'),
        this.$t('enabled-locations-map.find-my-location'),
      ]
    },
  },

  watch: {
    '$i18n.locale'(newLocale) {
      this.activeTab = this.tabs[0] // Reset to default tab on language change
      this.mapboxConfig.language = newLocale
      if (this.mapContext && this.mapContext.setLanguage) {
        this.mapContext.setLanguage(newLocale)
      }
    },
  },

  async created() {
    await Promise.all([
      this.getLocations(),
      this.getIx(),
    ])
    this.handleDeepLinks()
  },

  methods: {
    handleError(error) {
      this.error = error
    },
    setMapContext(context) {
      this.mapContext = context
    },
    setMapReadyStatus() {
      this.mapReady = true
      this.mapContext.resize()
    },
    async getLocations() {
      this.locationsLoading = true
      try {
        const status = process.env.VUE_APP_API_STATUS.split(',')
        const response = await axios(`${process.env.VUE_APP_API}?locationStatuses=${status.join('&locationStatuses=')}`)
        this.locationsList = response.data.data
        this.locationsLoading = false
      } catch (err) {
        this.error = err
      }
    },
    async getIx() {
      this.ixLoading = true
      try {
        const response = await axios('https://api.megaport.com/v2/ixp')
        this.ixList = response.data.data
        this.ixLoading = false
      } catch (err) {
        this.error = err
      }
    },
    handleDeepLinks() {
      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.get('locationId')) {
        this.deepLinkLocation = this.findLocationById(urlParams.get('locationId')) || {}
        this.$emit('deepLinkLocationUpdated', this.deepLinkLocation)
      }
      if (urlParams.get('hidePane')) {
        this.hidePane = true
      }
    },
    findLocationById(id) {
      return this.enabledLocations.find(location => location.id === Number(id))
    },
    handleFilterOption(option) {
      this.filterOptions = option
    },
    async setTooltipPosition() {
      const locationLegend = document.getElementById('location-legend')
      const tooltip = document.getElementById('tooltip')
      const arrowElement = document.getElementById('arrow')

      const position = await computePosition(locationLegend, tooltip, { placement: 'bottom', middleware: [
        offset(6),
        flip(),
        shift({ padding: 5 }),
        arrow({ element: arrowElement }),
      ] })

      // Put the tooltip in the right place
      Object.assign(tooltip.style, {
        left: `${position.x}px`,
        top: `${position.y}px`,
      })

      // Extract the arrow position and put it in the right place
      const { x: arrowX, y: arrowY } = position.middlewareData.arrow
      const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
      }[position.placement.split('-')[0]]

      Object.assign(arrowElement.style, {
        left: arrowX !== null ? `${arrowX}px` : '',
        top: arrowY !== null ? `${arrowY}px` : '',
        right: '',
        bottom: '',
        [staticSide]: '-4px',
      })
    },
    showTooltip() {
      const tooltip = document.getElementById('tooltip')
      const locationLegend = document.getElementById('location-legend')

      this.tooltipCleanup = autoUpdate(
        locationLegend,
        tooltip,
        this.setTooltipPosition
      )
      tooltip.style.display = 'block'
    },
    hideTooltip() {
      const tooltip = document.getElementById('tooltip')
      tooltip.style.display = 'none'
      this.tooltipCleanup()
    },
  },
}
</script>

<style lang="scss">
:root {
  --megaport-blue: #229df5;
  --megaport-slate: #414141;
  --megaport-plum: #1e0f44;
}

</style>
